import React, { useState, useEffect } from "react";
import { message, Select } from "antd";
import {
  UserOutlined,
  MailOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  PlusOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { TikTokIcon, InstagramIcon } from "../../design-system/components/data-display/EmailProviderIcons";
import styled, { createGlobalStyle } from "styled-components";

import { Modal } from "../../design-system/components/feedback/Modal";
import { Button } from "../../design-system/components/inputs/Button";
import { Input } from "../../design-system/components/inputs/Input";
import { LoadingState } from "../../design-system/components/feedback/LoadingState";
import creatorService, {
  CreateCreatorRequest,
  UpdateCreatorRequest,
  CreateContactRequest,
  platformService
} from "./creatorService";
import type { 
  CreatorPlatformType, 
  TikTokProfileData, 
  InstagramProfileData,
  Creator,
  CreatorContact
} from './creatorTypes';


import {
  colors,
  spacing,
  typography,
  radius,
} from "../../design-system/tokens";


interface CreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: 'create' | 'edit';
  creator?: Creator | null;
  ticketUuid?: string;
}


// Global styles to override modal defaults for creator modal
const CreatorModalStyles = createGlobalStyle`
  .creator-modal {
    background: ${colors.palette.gray[100]} !important;
    top: 2% !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    height: 96% !important;
    max-height: 96% !important;
    
    /* Remove default modal body padding */
    & > div:last-child {
      padding: 0 !important;
    }
  }
`;

// Simple form data structure using platform service interfaces directly
interface CreatorFormData {
  contacts: CreateContactRequest[];
  tiktok_data: Partial<TikTokProfileData>[];
  instagram_data: Partial<InstagramProfileData>[];
}

// Styled Components (unified)
const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const TopPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[2]} ${spacing[4]};
  background: ${colors.palette.gray[25]};
  border-bottom: 1px solid rgba(0, 0, 0, 0.02);
`;

const TitleSection = styled.div`
  flex: 1;
  max-width: 300px;
  
  h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.text.primary};
  }
`;

const ContentArea = styled.div`
  flex: 1;
  background: ${colors.palette.gray[100]};
  padding: ${spacing[2]} ${spacing[8]};
  display: flex;
  justify-content: center;
  overflow-y: auto;
`;

const ContentSheet = styled.div`
  width: 100%;
  max-width: 1000px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  min-height: 600px;
  display: flex;
  flex-direction: column;
  padding: ${spacing[6]};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing[1]};
  align-items: center;
`;

const SaveButton = styled.button`
  background: transparent;
  color: ${colors.text.primary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  line-height: 1;
  
  &:hover:not(:disabled) {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
  margin-bottom: ${spacing[8]};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${spacing[4]};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding-bottom: ${spacing[2]};
  border-bottom: 1px solid ${colors.border.secondary};
`;

const SectionIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.secondary};
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

const ItemCard = styled.div`
  border: 1px solid ${colors.border.secondary};
  border-radius: 8px;
  padding: ${spacing[3]};
  background: white;
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${colors.border.primary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }
`;

const PlatformAvatar = styled.div<{ $platform: string }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: ${props => {
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      case 'twitch': return '#9146ff';
      default: return 'linear-gradient(135deg, #635bff 0%, #1a73e8 100%)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 12px;
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
`;

const PlatformImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
`;

const PlatformInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlatformHandle = styled.div`
  font-weight: 500;
  color: ${colors.text.primary};
  font-size: 14px;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const PlatformStats = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  line-height: 1.2;
  margin-top: 2px;
`;

const PlatformIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const SimpleForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const FormRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

const FormLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  font-weight: 500;
  color: ${colors.text.primary};
  font-size: 14px;
  min-width: 100px;
  flex-shrink: 0;
`;

const SearchResult = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  padding: ${spacing[3]};
  border: 1px solid ${colors.border.secondary};
  border-radius: 8px;
  background: white;
  margin-top: ${spacing[2]};
`;

const ProfileImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
`;

const ProfileFallback = styled.div<{ $platform: string }>`
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: ${props => {
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      case 'twitch': return '#9146ff';
      default: return 'linear-gradient(135deg, #635bff 0%, #1a73e8 100%)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 14px;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileHandle = styled.div`
  font-weight: 500;
  color: ${colors.text.primary};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const ProfileStats = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-top: 2px;
`;

const SmartInput = styled.div<{ $status: 'idle' | 'searching' | 'found' | 'not_found' }>`
  position: relative;
  flex: 1;
  
  input {
    width: 100%;
    padding: 8px 32px 8px 12px;
    border: 1px solid ${props => {
      switch (props.$status) {
        case 'searching': return colors.palette.blue[300];
        case 'found': return colors.palette.green[300];
        case 'not_found': return colors.palette.red[300];
        default: return colors.border.secondary;
      }
    }};
    border-radius: 6px;
    font-size: 14px;
    transition: all 0.2s ease;
    background: ${props => {
      switch (props.$status) {
        case 'searching': return colors.palette.blue[50];
        case 'found': return colors.palette.green[50];
        case 'not_found': return colors.palette.red[50];
        default: return 'white';
      }
    }};
    
    &:focus {
      outline: none;
      border-color: ${props => {
        switch (props.$status) {
          case 'searching': return colors.palette.blue[400];
          case 'found': return colors.palette.green[400];
          case 'not_found': return colors.palette.red[400];
          default: return colors.palette.indigo[400];
        }
      }};
      box-shadow: 0 0 0 3px ${props => {
        switch (props.$status) {
          case 'searching': return colors.palette.blue[100];
          case 'found': return colors.palette.green[100];
          case 'not_found': return colors.palette.red[100];
          default: return colors.palette.indigo[100];
        }
      }};
    }
  }
`;

const StatusIcon = styled.div<{ $status: 'idle' | 'searching' | 'found' | 'not_found' }>`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 14px;
    height: 14px;
    color: ${props => {
      switch (props.$status) {
        case 'searching': return colors.palette.blue[500];
        case 'found': return colors.palette.green[500];
        case 'not_found': return colors.palette.red[500];
        default: return 'transparent';
      }
    }};
  }
  
  ${props => props.$status === 'searching' && `
    svg {
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `}
`;

const CompactSearchResult = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: ${spacing[2]};
  margin-top: 4px;
  border-radius: 6px;
  background: ${colors.palette.green[50]};
  border: 1px solid ${colors.palette.green[200]};
  font-size: 12px;
`;

const CompactProfileImage = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  object-fit: cover;
`;

const CompactProfileFallback = styled.div<{ $platform: string }>`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: ${props => {
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      case 'twitch': return '#9146ff';
      default: return 'linear-gradient(135deg, #635bff 0%, #1a73e8 100%)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 10px;
`;

const CompactProfileInfo = styled.div`
  flex: 1;
  color: ${colors.text.secondary};
  
  .handle {
    font-weight: 500;
    color: ${colors.text.primary};
  }
`;


const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.div<{ $status: 'idle' | 'searching' | 'found' | 'not_found'; $hasResult?: boolean }>`
  position: relative;
  width: 100%;
  min-height: 40px;
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'searching': return colors.palette.blue[300];
      case 'found': return colors.palette.green[300];
      case 'not_found': return colors.palette.red[300];
      default: return colors.border.secondary;
    }
  }};
  border-radius: 8px;
  background: ${props => {
    switch (props.$status) {
      case 'searching': return colors.palette.blue[50];
      case 'found': return colors.palette.green[50];
      case 'not_found': return colors.palette.red[50];
      default: return 'white';
    }
  }};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  padding: 8px 32px 8px 12px;
  cursor: ${props => props.$hasResult ? 'default' : 'text'};

  &:focus-within {
    outline: none;
    border-color: ${props => {
      switch (props.$status) {
        case 'searching': return colors.palette.blue[400];
        case 'found': return colors.palette.green[400];
        case 'not_found': return colors.palette.red[400];
        default: return colors.palette.indigo[400];
      }
    }};
    box-shadow: 0 0 0 3px ${props => {
      switch (props.$status) {
        case 'searching': return colors.palette.blue[100];
        case 'found': return colors.palette.green[100];
        case 'not_found': return colors.palette.red[100];
        default: return colors.palette.indigo[100];
      }
    }};
  }

  input {
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    flex: 1;
    color: ${colors.text.primary};

    &::placeholder {
      color: ${colors.text.tertiary};
    }
  }
`;

const VerifiedResult = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex: 1;
  font-size: 14px;
  color: ${colors.text.primary};
  line-height: 1.4;
`;

const ResultAvatar = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  object-fit: cover;
  flex-shrink: 0;
`;

const ResultFallback = styled.div<{ $platform: string }>`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  background: ${props => {
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      case 'twitch': return '#9146ff';
      default: return 'linear-gradient(135deg, #635bff 0%, #1a73e8 100%)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 8px;
  flex-shrink: 0;
`;

const ResultText = styled.span`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border: none;
  background: ${colors.palette.gray[200]};
  color: ${colors.text.tertiary};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.palette.gray[300]};
    color: ${colors.text.secondary};
  }

  svg {
    width: 12px;
    height: 12px;
  }

  ${SearchInput}:hover & {
    opacity: 1;
  }
`;

const VerifiedDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  padding: 8px 32px 8px 12px;
  border: 1px solid ${colors.palette.green[300]};
  border-radius: 6px;
  background: ${colors.palette.green[50]};
  font-size: 14px;
  position: relative;
`;

const VerifiedProfile = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex: 1;
`;

const VerifiedAvatar = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
`;

const VerifiedFallback = styled.div<{ $platform: string }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => {
    switch (props.$platform?.toLowerCase()) {
      case 'tiktok': return '#fe2c55';
      case 'instagram': return '#E4405F';
      case 'twitch': return '#9146ff';
      default: return 'linear-gradient(135deg, #635bff 0%, #1a73e8 100%)';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 10px;
`;

const VerifiedInfo = styled.div`
  .handle {
    font-weight: 500;
    color: ${colors.text.primary};
  }
  .stats {
    font-size: 12px;
    color: ${colors.text.secondary};
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  border: none;
  background: none;
  color: ${colors.text.tertiary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  
  &:hover {
    background: ${colors.palette.red[100]};
    color: ${colors.palette.red[600]};
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const AddedPlatformsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};
`;

const SearchForm = styled.div`
  display: flex;
  gap: ${spacing[2]};
  align-items: end;
  padding: ${spacing[2]};
  background: ${colors.background.secondary};
  border-radius: ${radius.sm};
  border: 1px solid ${colors.border.secondary};
`;

const AddContactForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  border: 1px solid ${colors.border.secondary};
  border-radius: ${radius.md};
  background: ${colors.background.secondary};
`;


export const CreatorModal: React.FC<CreatorModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode,
  creator,
  ticketUuid,
}) => {
  // Simple form state matching database structure
  const [formData, setFormData] = useState<CreatorFormData>({
    contacts: [],
    tiktok_data: [],
    instagram_data: [],
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Platform search state (smart)
  const [searchLoading, setSearchLoading] = useState<{[key: string]: boolean}>({});
  const [searchResults, setSearchResults] = useState<{[key: string]: any}>({});
  const [verificationStatus, setVerificationStatus] = useState<{[key: string]: 'idle' | 'searching' | 'found' | 'not_found'}>({});
  
  // Track input values separately to prevent loops
  const [inputValues, setInputValues] = useState({
    tiktok: '',
    instagram: ''
  });
  
  // Track email input separately
  const [emailInput, setEmailInput] = useState('');
  


  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && creator) {
        setFormData({
          contacts: creator.contacts?.map(c => ({
            type: c.type,
            value: c.value,
            role: c.role,
            status: c.status,
          })) || [],
          tiktok_data: creator.tiktok_data?.map(t => ({
            handle: t.handle,
            profile_image_url: t.profile_image_url,
            followers_count: t.followers_count,
            hearts_count: t.hearts_count,
            video_count: t.video_count,
            user_id: t.user_id,
            engagement_rate: t.engagement_rate,
          })) || [],
          instagram_data: creator.instagram_data?.map(i => ({
            handle: i.handle,
            profile_image_url: i.profile_image_url,
            followers_count: i.followers_count,
            user_id: i.user_id,
            engagement_rate: i.engagement_rate,
          })) || [],
        });
        // Initialize input values from existing data
        setInputValues({
          tiktok: creator.tiktok_data?.[0]?.handle || '',
          instagram: creator.instagram_data?.[0]?.handle || ''
        });
        // Set verification status for existing data
        setVerificationStatus({
          tiktok: creator.tiktok_data?.[0]?.handle ? 'found' : 'idle',
          instagram: creator.instagram_data?.[0]?.handle ? 'found' : 'idle'
        });
        // Set search results for existing data
        if (creator.tiktok_data?.[0]) {
          setSearchResults(prev => ({ ...prev, tiktok: creator.tiktok_data![0] }));
        }
        if (creator.instagram_data?.[0]) {
          setSearchResults(prev => ({ ...prev, instagram: creator.instagram_data![0] }));
        }
        // Initialize email input
        setEmailInput(creator.contacts?.find(c => c.type === 'email')?.value || '');
      } else {
        setFormData({
          contacts: [],
          tiktok_data: [],
          instagram_data: [],
        });
        setInputValues({
          tiktok: '',
          instagram: ''
        });
        setVerificationStatus({});
        setSearchResults({});
        setEmailInput('');
      }
    }
  }, [isOpen, mode, creator]);

  // Smart debounced search
  useEffect(() => {
    const timeouts: { [key: string]: NodeJS.Timeout } = {};

    const performSearch = async (platform: 'tiktok' | 'instagram', username: string) => {
      if (!username.trim() || username.length < 2) {
        setVerificationStatus(prev => ({ ...prev, [platform]: 'idle' }));
        setSearchResults(prev => ({ ...prev, [platform]: null }));
      return;
    }

      // Don't search if we already have this exact username verified
      const existingResult = searchResults[platform];
      if (existingResult && existingResult.handle === username && verificationStatus[platform] === 'found') {
      return;
    }

      setVerificationStatus(prev => ({ ...prev, [platform]: 'searching' }));
      
      try {
        let profile: any;
        
        if (platform === 'tiktok') {
          const result = await platformService.enrichTikTokProfile(username.trim());
          if (result.success && result.profile) {
            profile = {
              handle: result.profile.handle,
              profile_image_url: result.profile.profile_image_url,
              followers_count: result.profile.followers_count,
              hearts_count: result.profile.hearts_count,
              video_count: result.profile.video_count,
              user_id: result.profile.user_id,
              engagement_rate: result.profile.engagement_rate,
            };
          }
        } else if (platform === 'instagram') {
          const result = await platformService.enrichInstagramProfile(username.trim());
          if (result.success && result.profile) {
            profile = {
              handle: result.profile.handle,
              profile_image_url: result.profile.profile_image_url,
              followers_count: result.profile.followers_count,
              user_id: result.profile.user_id,
              engagement_rate: result.profile.engagement_rate,
            };
          }
        }
        
        if (profile) {
          setSearchResults(prev => ({ ...prev, [platform]: profile }));
          setVerificationStatus(prev => ({ ...prev, [platform]: 'found' }));
          // Auto-update form data with verified profile
          if (platform === 'tiktok') {
            setFormData(prev => ({ ...prev, tiktok_data: [profile] }));
          } else if (platform === 'instagram') {
            setFormData(prev => ({ ...prev, instagram_data: [profile] }));
          }
          // Platform verified successfully
        } else {
          setSearchResults(prev => ({ ...prev, [platform]: null }));
          setVerificationStatus(prev => ({ ...prev, [platform]: 'not_found' }));
        }
      } catch (error) {
        console.error("Error searching platform:", error);
        setSearchResults(prev => ({ ...prev, [platform]: null }));
        setVerificationStatus(prev => ({ ...prev, [platform]: 'not_found' }));
      }
    };

    // Set up debounced searches for each platform based on input values
    if (inputValues.tiktok && inputValues.tiktok.length >= 2) {
      clearTimeout(timeouts.tiktok);
      timeouts.tiktok = setTimeout(() => performSearch('tiktok', inputValues.tiktok), 800);
    }

    if (inputValues.instagram && inputValues.instagram.length >= 2) {
      clearTimeout(timeouts.instagram);
      timeouts.instagram = setTimeout(() => performSearch('instagram', inputValues.instagram), 800);
    }

    return () => {
      Object.values(timeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [inputValues, searchResults, verificationStatus]);

  // Helper function to format followers
  const formatFollowers = (count: number) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  // Platform helpers
  const handleClearPlatform = (platform: 'tiktok' | 'instagram') => {
    setInputValues(prev => ({ ...prev, [platform]: '' }));
    setVerificationStatus(prev => ({ ...prev, [platform]: 'idle' }));
    setSearchResults(prev => ({ ...prev, [platform]: null }));
    setFormData(prev => ({ ...prev, [`${platform}_data`]: [] }));
  };

  const getPlatformIcon = (platform: string, size = 18) => {
    switch (platform) {
      case 'tiktok': return <TikTokIcon size={size} />;
      case 'instagram': return <InstagramIcon size={size} />;
      default: return null;
    }
  };


  const handleSave = async () => {
    const totalPlatforms = formData.tiktok_data.length + formData.instagram_data.length;
    if (totalPlatforms === 0) {
      message.error('Please add at least one social media handle');
      return;
    }

    try {
      setSaving(true);
      
      if (mode === 'create') {
        // Direct database structure payload
        const createPayload: CreateCreatorRequest = {
          contacts: formData.contacts,
          tiktok_data: formData.tiktok_data,
          instagram_data: formData.instagram_data,
          ...(ticketUuid && { ticket_uuid: ticketUuid })
        };
        await creatorService.createCreator(createPayload);
        // message.success(`Creator created successfully${ticketUuid ? ' and linked to ticket' : ''}!`);
      } else if (creator) {
        // Direct database structure payload
        const updatePayload: UpdateCreatorRequest = {
          contacts: formData.contacts,
          tiktok_data: formData.tiktok_data,
          instagram_data: formData.instagram_data,
        };
        await creatorService.updateCreator(creator.id, updatePayload);
        // message.success('Creator updated successfully!');
      }
      
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving creator:', error);
      const msg = (error instanceof Error ? error.message : '') || 'Failed to create creator';
      if (msg.includes('plan') || msg.toLowerCase().includes('upgrade')) {
        // message.error('Upgrade subscription to add more creators');
      } else {
        // message.error(`Failed to ${mode} creator`);
      }
    } finally {
      setSaving(false);
    }
  };


  return (
    <>
      <CreatorModalStyles />
    <Modal
      isOpen={isOpen}
      onClose={onClose}
        size="full"
        height="full"
        showCloseButton={false}
        className="creator-modal"
      >
        <ModalContent>
          <TopPanel>
            <TitleSection>
              <h3>{mode === 'create' ? 'Add New Creator' : 'Edit Creator'}</h3>
            </TitleSection>

            <ActionButtons>
              <SecondaryButton
                onClick={onClose}
                disabled={saving}
              >
                <CloseOutlined />
            Cancel
              </SecondaryButton>
              <SaveButton
            onClick={handleSave}
            disabled={saving}
          >
                {saving ? (
                  <>Saving...</>
                ) : (
                  <>
                    <CheckOutlined />
                    {mode === 'create' ? 'Create Creator' : 'Save Changes'}
                  </>
                )}
              </SaveButton>
            </ActionButtons>
          </TopPanel>

          <ContentArea>
            <ContentSheet>
        {loading ? (
          <LoadingState />
        ) : (
          <>
            {/* Platforms Section */}
            <Section>
              <SectionHeader>
                <SectionTitle>
                  Social Media Handles
                </SectionTitle>
              </SectionHeader>

              {/* Platform List */}
              <SimpleForm>
                {/* TikTok */}
                <FormRow>
                  <FormLabel>
                    <TikTokIcon size={16} />
                  
                  </FormLabel>
                  <SearchContainer>
                    <SearchInput 
                      $status={verificationStatus.tiktok || 'idle'}
                      $hasResult={!!searchResults.tiktok}
                    >
                      {searchResults.tiktok && verificationStatus.tiktok === 'found' ? (
                        // Show verified result inline (looks like input text)
                        <VerifiedResult>
                          {searchResults.tiktok.profile_image_url ? (
                            <ResultAvatar 
                              src={searchResults.tiktok.profile_image_url} 
                              alt={searchResults.tiktok.handle} 
                      />
                    ) : (
                            <ResultFallback $platform="tiktok">
                              {searchResults.tiktok.handle?.charAt(0)?.toUpperCase() || '?'}
                            </ResultFallback>
                          )}
                          <ResultText>
                            @{searchResults.tiktok.handle}
                          </ResultText>
                        </VerifiedResult>
                      ) : (
                        // Show input for searching
                        <input
                          placeholder="Search TikTok username..."
                          value={inputValues.tiktok}
                          onChange={(e) => {
                            const value = e.target.value.replace('@', '');
                            setInputValues(prev => ({ ...prev, tiktok: value }));
                            if (!value) {
                              setFormData(prev => ({ ...prev, tiktok_data: [] }));
                              setSearchResults(prev => ({ ...prev, tiktok: null }));
                              setVerificationStatus(prev => ({ ...prev, tiktok: 'idle' }));
                            }
                          }}
                        />
                      )}
                      
                      {/* Status Icon or Clear Button */}
                      {searchResults.tiktok && verificationStatus.tiktok === 'found' ? (
                        <ClearButton onClick={() => handleClearPlatform('tiktok')}>
                          <CloseOutlined />
                        </ClearButton>
                      ) : (
                        <StatusIcon $status={verificationStatus.tiktok || 'idle'}>
                          {verificationStatus.tiktok === 'searching' && <LoadingOutlined />}
                          {verificationStatus.tiktok === 'not_found' && <ExclamationCircleOutlined />}
                        </StatusIcon>
                      )}
                    </SearchInput>
                  </SearchContainer>
                </FormRow>

                {/* Instagram */}
                <FormRow>
                  <FormLabel>
                    <InstagramIcon size={16} />
         
                  </FormLabel>
                  <SearchContainer>
                    <SearchInput 
                      $status={verificationStatus.instagram || 'idle'}
                      $hasResult={!!searchResults.instagram}
                    >
                      {searchResults.instagram && verificationStatus.instagram === 'found' ? (
                        // Show verified result inline (looks like input text)
                        <VerifiedResult>
                          {searchResults.instagram.profile_image_url ? (
                            <ResultAvatar 
                              src={searchResults.instagram.profile_image_url} 
                              alt={searchResults.instagram.handle} 
                            />
                          ) : (
                            <ResultFallback $platform="instagram">
                              {searchResults.instagram.handle?.charAt(0)?.toUpperCase() || '?'}
                            </ResultFallback>
                          )}
                          <ResultText>
                            @{searchResults.instagram.handle}
                          </ResultText>
                        </VerifiedResult>
                      ) : (
                        // Show input for searching
                        <input
                          placeholder="Search Instagram username..."
                          value={inputValues.instagram}
                          onChange={(e) => {
                            const value = e.target.value.replace('@', '');
                            setInputValues(prev => ({ ...prev, instagram: value }));
                            if (!value) {
                              setFormData(prev => ({ ...prev, instagram_data: [] }));
                              setSearchResults(prev => ({ ...prev, instagram: null }));
                              setVerificationStatus(prev => ({ ...prev, instagram: 'idle' }));
                            }
                          }}
                        />
                      )}
                      
                      {/* Status Icon or Clear Button */}
                      {searchResults.instagram && verificationStatus.instagram === 'found' ? (
                        <ClearButton onClick={() => handleClearPlatform('instagram')}>
                          <CloseOutlined />
                        </ClearButton>
                      ) : (
                        <StatusIcon $status={verificationStatus.instagram || 'idle'}>
                          {verificationStatus.instagram === 'searching' && <LoadingOutlined />}
                          {verificationStatus.instagram === 'not_found' && <ExclamationCircleOutlined />}
                        </StatusIcon>
                      )}
                    </SearchInput>
                  </SearchContainer>
                </FormRow>
              </SimpleForm>

            </Section>

            {/* Contact Section */}
            <Section>
              <SectionHeader>
                <SectionTitle>
                  Contact Information
                </SectionTitle>
              </SectionHeader>

              <SimpleForm>
                {/* Email Field */}
                <FormRow>
                  <FormLabel>
                    <MailOutlined />
                    Email
                  </FormLabel>
                  <SearchContainer>
                    <SearchInput 
                      $status="idle"
                      $hasResult={!!emailInput}
                    >
                      {/* Always show input for entering email */}
                      <input
                        placeholder="Enter email address..."
                        value={emailInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEmailInput(value);
                          // Update form data
                          const otherContacts = formData.contacts.filter(c => c.type !== 'email');
                          const newContacts = value ? [...otherContacts, { type: 'email' as const, value, role: 'creator' as const, status: 'verified' as const }] : otherContacts;
                          setFormData(prev => ({ ...prev, contacts: newContacts }));
                        }}
                      />
                      
                      {/* Clear Button */}
                      {emailInput && (
                        <ClearButton onClick={() => {
                          setEmailInput('');
                          const otherContacts = formData.contacts.filter(c => c.type !== 'email');
                          setFormData(prev => ({ ...prev, contacts: otherContacts }));
                        }}>
                          <CloseOutlined />
                        </ClearButton>
                      )}
                    </SearchInput>
                  </SearchContainer>
                </FormRow>
              </SimpleForm>
            </Section>
          </>
        )}
            </ContentSheet>
          </ContentArea>
      </ModalContent>
    </Modal>
  </>
  );
};
